import * as React from 'react'
import { useLocale, useRecording } from '../../providers'
import { Button, IconButton } from '../Buttons'
import { Timer } from '../Timer'
import {
  RecorderAudioInputWrapper,
  MicButtonWrapper,
  ReviewButtonsWrapper,
} from './styles'
import { useStatefulRef, useAudio } from '../../hooks'
import { clamp } from '../../utils'
import Bowser from "bowser"

const { useState, useEffect, useCallback } = React

const MAX_TIME = 3 * 60 * 1000 // 3 minutes

interface RecorderAudioInputProps {
  formValues: { name: string; location: string }
  onSubmitRequested: (audioUrl: string, audioDuration: number) => void
}

export const RecorderAudioInput: React.FC<RecorderAudioInputProps> = ({
  formValues,
  onSubmitRequested,
}) => {
  const [isInPlayback, setIsInPlayback] = useState(false)
  const [audioRef, setAudioRef] = useStatefulRef<HTMLAudioElement>(null)
  const audioRefNonNull = audioRef as React.RefObject<HTMLAudioElement>
  const startTime = React.useRef<number>(0)
  const duration = React.useRef<number>(0)
  const { getText } = useLocale()
  const { state: audioState, play, pause } = useAudio({ ref: audioRefNonNull })
  const {
    state,
    requestPermission,
    startRecording,
    stopRecording,
    resetRecording,
  } = useRecording()

  const {
    audio,
    isRecording,
    totalElapsed: totalRecordingElapsed,
    permissionGranted,
  } = state

  const recordingProgress = clamp(totalRecordingElapsed / MAX_TIME, 0, 1)

  const playbackProgress = isRecording
    ? 0
    : clamp((audioState.currentTime * 1000) / totalRecordingElapsed, 0, 1)


  //don't allow ios & safari users to attempt webm playback
  const unsupportedDevice = React.useMemo(() => {
    if (typeof navigator === 'undefined') return true
    const parser = Bowser.getParser(navigator.userAgent)
    return parser.getOSName() === 'iOS' || parser.getBrowserName() === 'Safari'
  }, [])


  /**
   * Ask for permission to record when the component mounts
   */
  useEffect(() => {
    if (permissionGranted === null) requestPermission()
  }, [permissionGranted])

  /**
   * Turn playback mode on & off
   */
  const enablePlayback = () => setIsInPlayback(true)
  const disablePlayback = () => setIsInPlayback(false)

  /**
   * Turn on playback mode when the audio element starts playing
   */
  useEffect(() => {
    if (audio && audioRef.current) {
      audioRef.current.addEventListener('play', enablePlayback)
      return () => audioRef.current?.removeEventListener('play', enablePlayback)
    }
  }, [audio])

  /**
   * Turn off playback mode when the recorder is recording
   * (this will fire if the user resets)
   */
  useEffect(() => {
    if (isRecording || recordingProgress === 0) disablePlayback()
  }, [isRecording, recordingProgress])

  /**
   * Stop the recording at the max length
   */
  useEffect(() => {
    if (recordingProgress === 1) stopRecording()
  }, [recordingProgress])

  const handleSubmitClick = () => {
    if (!audio) {
      console.log('No audio to submit')
      return
    }
    if (!formValues) {
      console.log('No form values provided')
      return
    }
    onSubmitRequested(audio, duration.current)
  }

  /**
   * Event Handlers
   */
  const handleMicClick = () => {
    if (isRecording) {
      duration.current = Date.now() - startTime.current
      stopRecording()

    } else {
      startRecording()
      startTime.current = Date.now()

    }
  }

  const handlePlayClick = useCallback(() => {
    if (audioState.isPlaying) {
      pause()
    } else {
      play()
    }
  }, [audioState.isPlaying, audioRef.current])

  if (state.permissionGranted === null) {
    return <p>{getText('loadingMessage')}...</p>
  }

  if (state.permissionGranted === false) {
    return <p>{getText('permissionsNeededMessage')}</p>
  }

  const buttonLabel = isRecording
    ? getText('stopRecordingCta')
    : recordingProgress > 0
      ? '\u00A0'
      : getText('startRecordingCta')

  return (
    <RecorderAudioInputWrapper>
      <MicButtonWrapper>
        <IconButton
          icon="microphone"
          onClick={handleMicClick}
          disableHover={true}
          disabled={isRecording === false && recordingProgress > 0}
          spinning={isRecording}
          label={buttonLabel}
        />
      </MicButtonWrapper>
      {isRecording || recordingProgress > 0 ? (
        <Timer
          recordingProgress={
            isRecording === false && recordingProgress > 0
              ? 1
              : recordingProgress
          }
          playbackProgress={playbackProgress}
          inPlayback={isInPlayback}
        />
      ) : null}
      {isRecording === false && recordingProgress > 0 ? (
        <ReviewButtonsWrapper>
          {!unsupportedDevice && (
            <Button
              onClick={handlePlayClick}
              disabled={audioState.isPlaying}
              label={getText('listenToRecordingCta')}
            />
          )}
          <Button
            onClick={resetRecording}
            disabled={audioState.isPlaying}
            label={getText('resetRecordingLabel')}
          />
          <Button
            onClick={handleSubmitClick}
            disabled={audioState.isPlaying}
            label={getText('submitRecordingLabel')}
          />
        </ReviewButtonsWrapper>
      ) : null}
      {audio ? (
        <div hidden>
          <audio ref={setAudioRef} controls={false} src={audio} />
        </div>
      ) : null}
    </RecorderAudioInputWrapper>
  )
}
